/**
 * Grid Map State Machine
 * Pure state transitions: createGridMapState(map) → state, applyInput(state, input) → newState
 */

import type { GridMap, GridMapState, MapInput, MoveDirection } from './types';
import { getEntity, moveEntity, toggleWall, setTerrain, addEntity, removeEntity, coordKey } from './grid';
import { computeVisibility, DEFAULT_VISION_RADIUS } from './visibility';
import { createFogGrid, createLightGrid, updateFog } from './fog';
import { tryMove } from './movement';
import {
  extractVisibleSet,
  evaluateStepTriggers,
  evaluateProximityTriggers,
  evaluateVisibleTriggers,
  evaluateInteractTriggers,
  applyTriggerEvents,
} from './triggers';

/**
 * Initialize a GridMapState from a GridMap.
 * Finds the player entity, computes initial visibility and fog.
 * Evaluates initial visible triggers (for triggers visible from spawn).
 */
export function createGridMapState(map: GridMap, playerId?: string): GridMapState {
  // Find or default the player entity
  const pid = playerId ?? map.entities.find(e => e.type === 'player')?.id;
  if (!pid) {
    throw new Error('No player entity found. Add an entity with type "player" to the map.');
  }

  const player = getEntity(map, pid);
  if (!player) {
    throw new Error(`Player entity "${pid}" not found in map entities.`);
  }

  const fogGrid = createFogGrid(map.width, map.height);
  const lightGrid = createLightGrid(map.width, map.height);

  const openDoors = new Set<string>();

  // Compute initial visibility from player position
  const radius = player.lightRadius ?? DEFAULT_VISION_RADIUS;
  const visibility = computeVisibility(map, player.x, player.y, radius, openDoors);
  updateFog(fogGrid, lightGrid, visibility);

  let state: GridMapState = {
    map,
    playerId: pid,
    fogGrid,
    lightGrid,
    openDoors,
    lockedDoors: new Set<string>(),
    moveCount: 0,
    firedTriggers: new Set<string>(),
    triggerFlags: new Set<string>(),
    pendingEvents: [],
  };

  // Evaluate initial triggers: step (spawn position) + visible (initial FOV)
  const triggers = map.triggers ?? [];
  if (triggers.length > 0) {
    const emptySet = new Set<string>();
    const currVisible = extractVisibleSet(fogGrid);

    const events = [
      ...evaluateStepTriggers(triggers, state.firedTriggers, state.triggerFlags, player.x, player.y),
      ...evaluateProximityTriggers(triggers, state.firedTriggers, state.triggerFlags, player.x, player.y),
      ...evaluateVisibleTriggers(triggers, state.firedTriggers, state.triggerFlags, emptySet, currVisible, player.x, player.y),
    ];

    state = applyTriggerEvents(state, events);
  }

  return state;
}

/**
 * Apply an input to the state and return the new state.
 * Always resets pendingEvents to [] before processing, then appends new events.
 */
export function applyInput(state: GridMapState, input: MapInput): GridMapState {
  // Clear pending events from previous frame
  state = state.pendingEvents.length > 0 ? { ...state, pendingEvents: [] } : state;

  switch (input.type) {
    case 'move':
      return applyMoveWithTriggers(state, input.direction);

    case 'interact':
      return applyInteract(state, input.x, input.y);

    case 'toggle_door': {
      const key = coordKey(input.x, input.y);
      const cell = state.map.cells[input.y]?.[input.x];
      if (!cell || cell.terrain !== 'door') return state;
      if (state.lockedDoors.has(key)) return state;

      const openDoors = new Set(state.openDoors);
      if (openDoors.has(key)) {
        openDoors.delete(key);
      } else {
        openDoors.add(key);
      }
      return recomputeVisibility({ ...state, openDoors });
    }

    case 'toggle_wall': {
      const newMap = cloneMap(state.map);
      toggleWall(newMap, input.x, input.y, input.direction);
      return recomputeVisibility({ ...state, map: newMap });
    }

    case 'set_terrain': {
      const newMap = cloneMap(state.map);
      setTerrain(newMap, input.x, input.y, input.terrain);
      return recomputeVisibility({ ...state, map: newMap });
    }

    case 'place_entity': {
      const newMap = addEntity(state.map, input.entity);
      return { ...state, map: newMap };
    }

    case 'remove_entity': {
      const newMap = removeEntity(state.map, input.entityId);
      return { ...state, map: newMap };
    }
  }
}

// ============================================================================
// MOVE + TRIGGERS
// ============================================================================

function applyMoveWithTriggers(state: GridMapState, direction: MoveDirection): GridMapState {
  const triggers = state.map.triggers ?? [];

  // Fast path: no triggers, just move
  if (triggers.length === 0) {
    return tryMove(state, direction);
  }

  // Snapshot previous visible set before movement
  const prevVisibleSet = extractVisibleSet(state.fogGrid);

  // Attempt movement
  const movedState = tryMove(state, direction);

  // No movement happened — return with cleared events
  if (movedState === state) return state;

  const player = getEntity(movedState.map, movedState.playerId);
  if (!player) return movedState;

  // Extract new visible set after movement
  const currVisibleSet = extractVisibleSet(movedState.fogGrid);

  // Evaluate all trigger types
  const events = [
    ...evaluateStepTriggers(triggers, movedState.firedTriggers, movedState.triggerFlags, player.x, player.y),
    ...evaluateProximityTriggers(triggers, movedState.firedTriggers, movedState.triggerFlags, player.x, player.y),
    ...evaluateVisibleTriggers(triggers, movedState.firedTriggers, movedState.triggerFlags, prevVisibleSet, currVisibleSet, player.x, player.y),
  ];

  let result = applyTriggerEvents(movedState, events);

  // Handle teleport effects — move player and recompute visibility
  const teleportEvent = events.find(e => e.effect.type === 'teleport');
  if (teleportEvent && teleportEvent.effect.type === 'teleport') {
    const { targetX, targetY } = teleportEvent.effect;
    const newMap = moveEntity(result.map, result.playerId, targetX, targetY);
    result = recomputeVisibility({ ...result, map: newMap, moveCount: result.moveCount + 1 });
  }

  return result;
}

// ============================================================================
// INTERACT
// ============================================================================

function applyInteract(state: GridMapState, targetX: number, targetY: number): GridMapState {
  const player = getEntity(state.map, state.playerId);
  if (!player) return state;

  // Must be adjacent (Manhattan distance ≤ 1) or on same cell
  const dist = Math.abs(targetX - player.x) + Math.abs(targetY - player.y);
  if (dist > 1) return state;

  const triggers = state.map.triggers ?? [];
  if (triggers.length === 0) return state;

  const events = evaluateInteractTriggers(
    triggers, state.firedTriggers, state.triggerFlags,
    targetX, targetY, player.x, player.y,
  );

  return applyTriggerEvents(state, events);
}

// ============================================================================
// HELPERS
// ============================================================================

/** Recompute visibility from the player's current position. */
function recomputeVisibility(state: GridMapState): GridMapState {
  const player = getEntity(state.map, state.playerId);
  if (!player) return state;

  const radius = player.lightRadius ?? DEFAULT_VISION_RADIUS;
  const visibility = computeVisibility(state.map, player.x, player.y, radius, state.openDoors);
  const fogGrid = state.fogGrid.map(row => [...row]);
  const lightGrid = state.lightGrid.map(row => [...row]);
  updateFog(fogGrid, lightGrid, visibility);

  return { ...state, fogGrid, lightGrid };
}

/** Deep clone a GridMap (cells are mutable during generation). */
function cloneMap(map: GridMap): GridMap {
  return {
    ...map,
    cells: map.cells.map(row =>
      row.map(cell => ({
        ...cell,
        walls: { ...cell.walls },
      }))
    ),
    entities: map.entities.map(e => ({ ...e })),
  };
}
